/**
 * How big a shared photo is drawn in a conversation, and in what shape.
 *
 * Four attempts, worth recording because each one failed for a different
 * reason and the last two look like reversals of each other:
 *
 *  1. A fixed 200×200 box with `contain`. Small, and — because almost no photo
 *     is square — ringed with empty space on two sides.
 *  2. The box cut to the photo's shape at 320 wide. Nothing was wasted, but at
 *     that size a wide photo is a strip rather than a picture.
 *  3. A 280 square that the photo FILLED, cropping. It cut the subject out of
 *     anything wide: a banner arrived as three letters of its own name.
 *  4. The same square with a blurred copy of the photo behind it. Nothing was
 *     cropped, but the blur was noise.
 *
 * Where it landed: the photo's own shape, drawn large. Not a thumbnail with a
 * bubble around it — the picture is the message, so it gets the room a message
 * gets, bounded so one photo cannot take over the thread.
 */

/**
 * As wide as a photo is ever drawn, whatever the window can spare.
 *
 * The room a photo gets comes from removing the bubble's padding around it
 * rather than from raising this: the picture fills the bubble edge to edge, so
 * every point of the footprint is picture. Widening this instead would make the
 * message bigger without making the image better.
 */
export const PHOTO_MAX_WIDTH = 320;

/**
 * And as tall. A portrait photo at full width would be over 400pt of one
 * message, pushing the rest of the conversation off-screen; past this the
 * photo narrows instead, which keeps all of it visible without letting one
 * bubble own the thread.
 */
export const PHOTO_MAX_HEIGHT = 400;

/** The shape a photo falls back to when the server never recorded its size. */
export const PHOTO_FALLBACK_RATIO = 4 / 3;

export type PhotoBox = { width: number; height: number };

/**
 * The box for a photo, given its real pixel size and the width available.
 *
 * `available` is what the bubble can actually offer — the message column minus
 * its padding — so a narrow phone gets a photo that fits rather than one that
 * is clipped by the bubble it sits in.
 */
export function photoBox(
  width: number | null | undefined,
  height: number | null | undefined,
  available: number,
): PhotoBox {
  // Unknown, zero or nonsense dimensions all mean the same thing: we cannot
  // cut the box to the picture, so use a neutral landscape shape. Guarded
  // rather than trusted — a zero height here would divide to Infinity and
  // render nothing at all.
  const ratio =
    typeof width === "number" &&
    typeof height === "number" &&
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0
      ? width / height
      : PHOTO_FALLBACK_RATIO;

  // Never wider than the ceiling, and never wider than the bubble allows.
  const maxWidth = Math.max(
    1,
    Math.min(PHOTO_MAX_WIDTH, Number.isFinite(available) ? available : PHOTO_MAX_WIDTH),
  );

  let boxWidth = maxWidth;
  let boxHeight = boxWidth / ratio;

  // Tall photos are bounded by height instead, narrowing rather than cropping.
  if (boxHeight > PHOTO_MAX_HEIGHT) {
    boxHeight = PHOTO_MAX_HEIGHT;
    boxWidth = boxHeight * ratio;
  }

  return { width: Math.round(boxWidth), height: Math.round(boxHeight) };
}

/**
 * How much width a message bubble can give its contents.
 *
 * The thread is capped at `columnMax` and centred, each bubble stack takes 78%
 * of that, and the bubble itself pads both sides. Computed rather than assumed
 * because on a phone the window is the constraint and on the web the column is,
 * and a photo sized for the wrong one is either clipped or needlessly small.
 */
export function availablePhotoWidth(
  windowWidth: number,
  columnMax: number,
  stackFraction: number,
  bubblePadding: number,
): number {
  const column = Math.min(windowWidth, columnMax);
  return Math.max(1, column * stackFraction - bubblePadding * 2);
}
